import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocketStore } from '../stores/socketStore';
import { 
  Scissors, 
  Lightbulb, 
  Calendar, 
  Shield, 
  Zap, 
  RefreshCw,
  Snowflake,
  Users,
  X
} from 'lucide-react';

export interface PowerUp {
  id: string;
  name: string;
  description: string;
  icon: string;
  cost: number;
  cooldown: number;
  maxPerGame: number;
  targetRequired: boolean;
  available: boolean;
  remainingCooldown: number;
  usesLeft: number;
}

interface PowerUpsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  players: { id: string; name: string }[];
  myId: string;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  '50-50': <Scissors className="w-5 h-5" />,
  'hint-letter': <Lightbulb className="w-5 h-5" />,
  'hint-year': <Calendar className="w-5 h-5" />,
  'steal': <span className="text-lg">🦹</span>,
  'shield': <Shield className="w-5 h-5" />,
  'double': <Zap className="w-5 h-5" />,
  'replay': <RefreshCw className="w-5 h-5" />,
  'freeze': <Snowflake className="w-5 h-5" />,
};

export default function PowerUpsMenu({ isOpen, onClose, players, myId }: PowerUpsMenuProps) {
  const { socket } = useSocketStore();
  const [powerUps, setPowerUps] = useState<PowerUp[]>([]);
  const [points, setPoints] = useState(0);
  const [selectedPowerUp, setSelectedPowerUp] = useState<PowerUp | null>(null);
  const [showTargetSelect, setShowTargetSelect] = useState(false);
  
  // Récupérer la liste des power-ups
  useEffect(() => {
    if (!socket || !isOpen) return;
    
    socket.emit('game:getPowerUps');
    
    const handleList = (data: { powerUps: PowerUp[]; points: number }) => {
      setPowerUps(data.powerUps);
      setPoints(data.points);
    };
    
    socket.on('game:powerUpsList', handleList);
    
    // Refresh toutes les 5 secondes
    const interval = setInterval(() => {
      socket.emit('game:getPowerUps');
    }, 5000);
    
    return () => {
      socket.off('game:powerUpsList', handleList);
      clearInterval(interval);
    };
  }, [socket, isOpen]);
  
  const handleUsePowerUp = (powerUp: PowerUp, targetId?: string) => {
    if (!socket) return;
    
    socket.emit('game:usePowerUp', { 
      type: powerUp.id, 
      targetId 
    });
    
    setShowTargetSelect(false);
    setSelectedPowerUp(null);
    onClose();
  };
  
  const handlePowerUpClick = (powerUp: PowerUp) => {
    if (!powerUp.available) return;
    
    if (powerUp.targetRequired) {
      setSelectedPowerUp(powerUp);
      setShowTargetSelect(true);
    } else {
      handleUsePowerUp(powerUp);
    }
  };
  
  if (!isOpen) return null;
  
  const otherPlayers = players.filter(p => p.id !== myId);
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-dark-800 rounded-xl border border-dark-600 max-w-md w-full max-h-[80vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-dark-600">
            <div>
              <h2 className="font-display text-xl font-bold">Power-Ups</h2>
              <p className="text-sm text-dark-400">Points: <span className="text-primary-400 font-bold">{points}</span></p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Power-ups list */}
          {!showTargetSelect ? (
            <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
              {powerUps.map(powerUp => (
                <motion.button
                  key={powerUp.id}
                  onClick={() => handlePowerUpClick(powerUp)}
                  disabled={!powerUp.available}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all
                    ${powerUp.available 
                      ? 'bg-dark-700 border-dark-500 hover:border-primary-500 hover:bg-dark-600' 
                      : 'bg-dark-800 border-dark-700 opacity-50 cursor-not-allowed'
                    }`}
                  whileHover={powerUp.available ? { scale: 1.02 } : {}}
                  whileTap={powerUp.available ? { scale: 0.98 } : {}}
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                    ${powerUp.available ? 'bg-primary-500/20 text-primary-400' : 'bg-dark-600 text-dark-400'}`}
                  >
                    {ICON_MAP[powerUp.id] || <span className="text-lg">{powerUp.icon}</span>}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{powerUp.name}</span>
                      {powerUp.cost > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                          -{powerUp.cost} pts
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-dark-400">{powerUp.description}</p>
                  </div>
                  
                  {/* Status */}
                  <div className="text-right">
                    {powerUp.remainingCooldown > 0 ? (
                      <span className="text-xs text-orange-400">{powerUp.remainingCooldown}s</span>
                    ) : (
                      <span className="text-xs text-dark-400">{powerUp.usesLeft} restants</span>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            // Target selection
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <button 
                  onClick={() => setShowTargetSelect(false)}
                  className="text-dark-400 hover:text-white"
                >
                  ← Retour
                </button>
                <span className="text-sm">Choisir une cible pour {selectedPowerUp?.name}</span>
              </div>
              
              <div className="space-y-2">
                {otherPlayers.map(player => (
                  <button
                    key={player.id}
                    onClick={() => handleUsePowerUp(selectedPowerUp!, player.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-dark-700 border border-dark-500 hover:border-red-500 hover:bg-dark-600 transition-all"
                  >
                    <Users className="w-5 h-5 text-dark-400" />
                    <span>{player.name}</span>
                  </button>
                ))}
                
                {otherPlayers.length === 0 && (
                  <p className="text-center text-dark-400 py-4">Aucun autre joueur</p>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
